import React, { useState, useRef } from "react";
import {
  Modal,
  TextInput,
  Button,
  Group,
  NumberInput,
  Text,
  Textarea,
} from "@mantine/core";
import { Dropzone } from "@mantine/dropzone";
import { IconPhoto } from "@tabler/icons-react";
import classes from "./add.module.css";
import { RowDataMerchandise } from "./types";

interface AddMerchandiseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddMerchandise: (newMerchandiseData: RowDataMerchandise) => void;
}

const AddMerchandiseModal: React.FC<AddMerchandiseModalProps> = ({
  isOpen,
  onClose,
  onAddMerchandise,
}) => {
  const [newMerchandise, setNewMerchandise] = useState<RowDataMerchandise>({
    id: "",
    merchandiseName: "",
    merchandiseModel: "",
    merchandiseDesc: "",
    imageSrc: "",
    quantity: "",
    cost: "",
    action: "Edit",
  });
  const imageDropzoneRef = useRef<() => void>(null);

  const handleImageUpload = (files: File[]) => {
    const file = files[0];
    if (file) {
      setNewMerchandise({ ...newMerchandise, imageSrc: file });
    }
  };

  const handleAddMerchandise = () => {
    // Check if any required fields are empty
    if (
      !newMerchandise.merchandiseName ||
      !newMerchandise.merchandiseModel ||
      !newMerchandise.merchandiseDesc ||
      !newMerchandise.imageSrc ||
      !newMerchandise.quantity ||
      !newMerchandise.cost
    ) {
      alert("Please fill in all the required fields");
      return;
    }

    onAddMerchandise(newMerchandise);

    // Clear inputs and reset state
    setNewMerchandise({
      id: "",
      merchandiseName: "",
      merchandiseModel: "",
      merchandiseDesc: "",
      imageSrc: "",
      quantity: "",
      cost: "",
      action: "Edit",
    });

    onClose();
  };

  const handleClose = () => {
    // Clear inputs and reset state
    setNewMerchandise({
      id: "",
      merchandiseName: "",
      merchandiseModel: "",
      merchandiseDesc: "",
      imageSrc: "",
      quantity: "",
      cost: "",
      action: "Edit",
    });

    // Call the onClose function
    onClose();
  };

  return (
    <Modal
      style={{ zIndex: "100" }}
      title={
        <div style={{ textAlign: "center", fontWeight: "bold" }}>
          New Merchandise
        </div>
      }
      opened={isOpen}
      onClose={handleClose}
    >

    <Text className={classes.Text}>
        Image <span className={classes.format}>(.jpg, .png)</span>
      </Text>
      <Text fw={500} fz={12} c={'#5d5d5d'}>1000px x 500px</Text>
      <Dropzone
        onDrop={handleImageUpload}
        accept={["image/*"]}
        openRef={imageDropzoneRef}
      >
        <div className={classes.imageContainer}>
          {newMerchandise.imageSrc ? (
            <img
              src={URL.createObjectURL(newMerchandise.imageSrc)}
              alt="Image"
              className={classes.imageSize}
            />
          ) : (
            <p>
              <IconPhoto />
            </p>
          )}
        </div>
      </Dropzone>
      <Group>
        <Button
          onClick={() => imageDropzoneRef.current?.()}
          className={classes.imageButton}
        >
          <IconPhoto />
          Upload Image
        </Button>
      </Group>

      <TextInput
        label="Merchandise Name"
        placeholder="Merchandise Name"
        mb="sm"
        maxLength={255}
        value={newMerchandise.merchandiseName}
        onChange={(event) =>
          setNewMerchandise({
            ...newMerchandise,
            merchandiseName: event.currentTarget.value,
          })
        }
      />

      <TextInput
        label="Merchandise Model"
        placeholder="Merchandise Model"
        mb="sm"
        maxLength={255}
        value={newMerchandise.merchandiseModel}
        onChange={(event) =>
          setNewMerchandise({
            ...newMerchandise,
            merchandiseModel: event.currentTarget.value,
          })
        }
      />

    <Textarea
        label="Merchandise Description"
        placeholder="Merchandise Description"
        mb="sm"
        autosize={true}
        minRows={4}
        value={newMerchandise.merchandiseDesc}
        onChange={(event) =>
          setNewMerchandise({
            ...newMerchandise,
            merchandiseDesc: event.currentTarget.value,
          })
        }
      />


      <NumberInput
        label="Quantity"
        placeholder="Quantity"
        mb="sm"
        allowDecimal={false}
        allowNegative={false}
        maxLength={255}
        value={newMerchandise.quantity.toString()}
        onChange={(value) =>
          setNewMerchandise({
            ...newMerchandise,
            quantity: value.toString(),
          })
        }
      />

      <NumberInput
        label="Cost"
        placeholder="Cost"
        mb="sm"
        maxLength={255}
        allowNegative={false}
        decimalScale={2}
        value={newMerchandise.cost.toString()}
        onChange={(value) =>
          setNewMerchandise({
            ...newMerchandise,
            cost: value.toString(),
          })
        }
      />

      <Group className={classes.buttonBottom}>
        <Button onClick={() => onClose()} className={classes.buttonBack}>
          Back
        </Button>
        <Button onClick={handleAddMerchandise} className={classes.buttonSubmit}>
          Submit
        </Button>
      </Group>
    </Modal>
  );
};

export default AddMerchandiseModal;
